/**
 * CardAiSummary — AI-powered contract summary card
 *
 * Displays a cached AI summary (markdown) or a CTA to generate one.
 * Supports AI Mode for document selection and prompt editing.
 */

// react
import { useCallback, useEffect, useMemo, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// markdown (custom zero-dependency renderer → MUI components)
import { MarkdownRenderer } from '../../ui/MarkdownRenderer/MarkdownRenderer';

// components
import { AiDisclaimerBanner } from '../../ui/AiDisclaimerBanner/AiDisclaimerBanner';

// mui
import {
	alpha,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Collapse,
	Divider,
	IconButton,
	LinearProgress,
	Paper,
	Stack,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	AutoAwesome as IconSparkle,
	Close as IconClose,
	DataObject as IconJson,
	Description as IconDoc,
	Edit as IconEdit,
	ErrorOutline as IconError,
	ExpandMore as IconExpand,
	Refresh as IconRefresh,
	SelectAll as IconSelectAll,
	Translate as IconTranslate,
} from '@mui/icons-material';

// services
import { fetchAiBlob, fetchAiDocumentResults, sendAiPrompt } from '../../../services/aiApi';
import type {
	AiDocument,
	AiDocumentResultBlob,
} from '../../pages/ProjectV2/Documents/documentTypes';

// design system
import { radius } from '../../../utils/designSystem';

// types
import type { V2Contract, V2ContractPayload, V2ContractAiSummary } from '../../../@types';

// Language display names

const LANG_NAMES: Record<string, string> = {
	am: 'አማርኛ',
	bn: 'বাংলা',
	en: 'English',
	es: 'Español',
	fil: 'Filipino',
	fr: 'Français',
	ja: '日本語',
	pt: 'Português',
	ro: 'Română',
	sw: 'Kiswahili',
	zh: '中文',
};

const getLangName = (code: string): string => LANG_NAMES[code] || code.toUpperCase();

// Constants

// System-level instruction prepended to every prompt (not visible/editable in UI).
// Produces a narrative-style executive summary (no bullets/headings) modeled on
// the FM/Disbursement Specialist persona summaries.
const SYSTEM_PROMPT = `You are a World Bank contract analyst writing a concise executive summary.

Output rules — MUST follow:
- Write in flowing narrative prose: 2 to 3 short paragraphs, ~200–280 words total.
- Do NOT use bullet points, numbered lists, headings, bold labels, or any markdown structure unless explicitly instructed.
- Do NOT use markdown emphasis (**, __, #). Plain sentences only.
- First paragraph: what the contract is, who the parties are, total value, and key dates.
- Second paragraph: the main deliverables, payment/disbursement structure, and timelines.
- Third paragraph (optional): notable risks, conditions, or open items.
- Do not speculate. If a fact is unavailable, simply omit it — do not write "Not available" or placeholders.
- No greetings, disclaimers, or meta-commentary.
`;

const DEFAULT_PROMPT = `Write a narrative executive summary of this contract in 2–3 short paragraphs of plain prose. Cover: the contract's purpose and parties, key amounts and dates, the main deliverables and payment/disbursement structure, and any notable risks or conditions. Do not use bullet points, headings, or any markdown formatting — write it as if briefing a colleague in flowing sentences.`;

const MAX_CHARS_PER_DOC = 4000;

/**
 * Serialize the contract API response as JSON context for the AI prompt.
 * Strips the cached aiSummary to avoid circular/redundant data.
 */
const buildContractDataContext = (c: V2Contract): string => {
	const { aiSummary, ...rest } = c;
	return `Contract Detail (API JSON):\n${JSON.stringify(rest, null, 2)}`;
};

// Min text size per project standard
const TEXT_BASE = '1.25rem';
const TEXT_LG = '1.5rem';
const TEXT_SECTION = '1.3rem';

// Types

interface DocInsight {
	docId: string;
	filename: string;
	jobId: string;
	hasInsights: boolean;
	textLength: number;
	text: string;
	stage: string;
}

interface CardAiSummaryProps {
	contract: V2Contract;
	contractDocs: AiDocument[];
	docsLoading: boolean;
	onUpdate: (payload: Partial<V2ContractPayload>) => Promise<unknown>;
}

// Component

export const CardAiSummary = ({
	contract,
	contractDocs,
	docsLoading,
	onUpdate,
}: CardAiSummaryProps) => {
	const theme = useTheme();

	// i18n
	const { i18n } = useTranslation();
	const currentLang = i18n.language || 'en';

	// State
	const [aiMode, setAiMode] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
	const [generating, setGenerating] = useState(false);
	const [translating, setTranslating] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [docInsights, setDocInsights] = useState<DocInsight[]>([]);
	const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
	const [insightsLoading, setInsightsLoading] = useState(false);
	const [showPromptEditor, setShowPromptEditor] = useState(false);
	const [includeContractData, setIncludeContractData] = useState(true);
	const [includePrompt, setIncludePrompt] = useState(true);

	// Multi-lang AI summary helpers
	const aiSummary = contract.aiSummary as V2ContractAiSummary | null;
	const currentTranslation = aiSummary?.translations?.[currentLang] || null;
	const sourceTranslation = aiSummary ? aiSummary.translations?.[aiSummary.current] || null : null;
	const displayTranslation = currentTranslation || sourceTranslation;
	const displayLang = currentTranslation ? currentLang : aiSummary?.current || 'en';
	const needsTranslation =
		!currentTranslation && !!sourceTranslation && displayLang !== currentLang;

	const hasDocs = contractDocs.length > 0;
	const docsWithJobs = useMemo(() => contractDocs.filter((d) => d.job_id), [contractDocs]);

	// Sync prompt when contract / language changes
	useEffect(() => {
		const t = aiSummary?.translations?.[currentLang] || sourceTranslation;
		if (t?.prompt && t.prompt !== 'translated') {
			setPrompt(t.prompt);
		}
	}, [aiSummary, currentLang, sourceTranslation]);

	// =========================================================================
	// Fetch document insights (blob text) for each doc
	// =========================================================================

	const loadDocInsights = useCallback(async () => {
		if (docsWithJobs.length === 0) return;
		setInsightsLoading(true);

		const insights: DocInsight[] = [];

		for (const doc of docsWithJobs) {
			if (!doc.job_id) continue;

			try {
				const res = await fetchAiDocumentResults(doc.job_id);
				const blobs = res.blobs || [];

				// Prefer gold → pre-gold → silver
				const stages = ['gold', 'pre-gold', 'silver'];
				let bestText = '';
				let bestStage = '';

				for (const stage of stages) {
					const stageBlobs = blobs.filter(
						(b: AiDocumentResultBlob) =>
							b.stage === stage && (b.blob_name?.endsWith('.json') || b.blob_name?.endsWith('.txt'))
					);

					for (const blob of stageBlobs) {
						if (!blob.blob_name) continue;
						const slashIdx = blob.blob_name.indexOf('/');
						const container =
							slashIdx > 0 ? blob.blob_name.slice(0, slashIdx) : blob.stage || 'gold';
						const blobPath = slashIdx > 0 ? blob.blob_name.slice(slashIdx + 1) : blob.blob_name;

						try {
							const blobRes = await fetchAiBlob(container, blobPath);
							const content = blobRes.content;

							let text = '';
							if (typeof content === 'string') {
								text = content;
							} else if (Array.isArray(content)) {
								text = content
									.map((item: { Summary?: string; Persona?: string }) => {
										if (item?.Summary) return item.Summary;
										if (item?.Persona) return `${item.Persona}: ${JSON.stringify(item)}`;
										return JSON.stringify(item);
									})
									.join('\n\n');
							} else if (content && typeof content === 'object') {
								text = JSON.stringify(content, null, 2);
							}

							if (text.length > bestText.length) {
								bestText = text;
								bestStage = stage;
							}
						} catch {
							// Blob fetch failed — skip
						}
					}

					if (bestText) break;
				}

				insights.push({
					docId: doc.id,
					filename: doc.filename,
					jobId: doc.job_id,
					hasInsights: bestText.length > 0,
					textLength: bestText.length,
					text: bestText,
					stage: bestStage,
				});
			} catch {
				insights.push({
					docId: doc.id,
					filename: doc.filename,
					jobId: doc.job_id ?? '',
					hasInsights: false,
					textLength: 0,
					text: '',
					stage: '',
				});
			}
		}

		setDocInsights(insights);
		setSelectedDocs(new Set(insights.filter((d) => d.hasInsights).map((d) => d.docId)));
		setInsightsLoading(false);
	}, [docsWithJobs]);

	// =========================================================================
	// Enter AI Mode
	// =========================================================================

	const handleOpenAiMode = useCallback(() => {
		setAiMode(true);
		setExpanded(true);
		setError(null);
		if (docInsights.length === 0) {
			loadDocInsights();
		}
	}, [docInsights.length, loadDocInsights]);

	// =========================================================================
	// Generate summary
	// =========================================================================

	const handleGenerate = useCallback(async () => {
		setGenerating(true);
		setError(null);

		try {
			// Build prompt — docs, contract data, and prompt are all optional
			const parts: string[] = [];

			if (includePrompt) {
				parts.push(prompt);
			}

			if (includeContractData) {
				parts.push(`---\n\n${buildContractDataContext(contract)}`);
			}

			const selectedInsights = docInsights.filter((d) => selectedDocs.has(d.docId) && d.text);
			if (selectedInsights.length > 0) {
				const documentTexts = selectedInsights
					.map((d) => {
						const truncated =
							d.text.length > MAX_CHARS_PER_DOC
								? d.text.slice(0, MAX_CHARS_PER_DOC) + '\n...[truncated]'
								: d.text;
						return `--- ${d.filename} (${d.stage}) ---\n${truncated}`;
					})
					.join('\n\n');
				parts.push(`---\n\nDocuments:\n\n${documentTexts}`);
			}

			// Add language instruction so the AI responds in the current UI language
			const langInstruction =
				currentLang !== 'en'
					? `\nIMPORTANT: Write the ENTIRE response in ${getLangName(currentLang)} (${currentLang}). Do not mix languages.\n`
					: '';

			const fullPrompt = `${SYSTEM_PROMPT}${langInstruction}\n${parts.join('\n\n')}`;
			const response = await sendAiPrompt({ prompt: fullPrompt });

			const updatedAiSummary: V2ContractAiSummary = {
				current: currentLang,
				translations: {
					...(aiSummary?.translations || {}),
					[currentLang]: {
						prompt,
						results: response.response,
					},
				},
			};

			await onUpdate({ aiSummary: updatedAiSummary } as Partial<V2ContractPayload>);
			setAiMode(false);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to generate summary');
		} finally {
			setGenerating(false);
		}
	}, [
		docInsights,
		selectedDocs,
		prompt,
		onUpdate,
		includeContractData,
		includePrompt,
		contract,
		currentLang,
		aiSummary,
	]);

	// =========================================================================
	// Translate existing summary into the current UI language
	// =========================================================================

	const handleTranslate = useCallback(async () => {
		if (!sourceTranslation || !aiSummary) return;
		setTranslating(true);
		setError(null);

		try {
			const translatePrompt = `${SYSTEM_PROMPT}
IMPORTANT: Translate the following markdown summary ENTIRELY into ${getLangName(currentLang)} (${currentLang}).
Preserve all markdown formatting (headings, bullets, bold). Do not add or remove content — only translate.

${sourceTranslation.results}`;

			const response = await sendAiPrompt({ prompt: translatePrompt });

			const updatedAiSummary: V2ContractAiSummary = {
				current: aiSummary.current,
				translations: {
					...aiSummary.translations,
					[currentLang]: {
						prompt: `translated from ${aiSummary.current}`,
						results: response.response,
					},
				},
			};

			await onUpdate({ aiSummary: updatedAiSummary } as Partial<V2ContractPayload>);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to translate summary');
		} finally {
			setTranslating(false);
		}
	}, [sourceTranslation, aiSummary, currentLang, onUpdate]);

	// =========================================================================
	// Toggle helpers
	// =========================================================================

	const toggleDoc = (docId: string) => {
		setSelectedDocs((prev) => {
			const next = new Set(prev);
			if (next.has(docId)) next.delete(docId);
			else next.add(docId);
			return next;
		});
	};

	const toggleAll = () => {
		const available = docInsights.filter((d) => d.hasInsights).map((d) => d.docId);
		const allDocsSelected = selectedDocs.size === available.length && available.length > 0;
		const allOn = allDocsSelected && includeContractData && includePrompt;

		if (allOn) {
			// Deselect everything
			setSelectedDocs(new Set());
			setIncludeContractData(false);
			setIncludePrompt(false);
		} else {
			// Select everything
			setSelectedDocs(new Set(available));
			setIncludeContractData(true);
			setIncludePrompt(true);
		}
	};

	const contractDataChars = useMemo(() => buildContractDataContext(contract).length, [contract]);

	// =========================================================================
	// Render
	// =========================================================================

	// Dynamic subtitle reflecting what context is included
	const subtitle = useMemo(() => {
		if (aiMode) {
			const parts: string[] = [];
			const selCount = selectedDocs.size;
			if (selCount > 0) parts.push(`${selCount} doc${selCount !== 1 ? 's' : ''}`);
			if (includeContractData) parts.push('contract data');
			if (includePrompt) parts.push('prompt');
			if (!parts.length) return 'No context selected';
			return `Will use: ${parts.join(' + ')}`;
		}
		if (displayTranslation) {
			const langLabel = getLangName(displayLang);
			if (needsTranslation) {
				return `Showing ${langLabel} — translate to ${getLangName(currentLang)}`;
			}
			return langLabel;
		}
		return null;
	}, [
		aiMode,
		selectedDocs.size,
		includeContractData,
		includePrompt,
		displayTranslation,
		displayLang,
		needsTranslation,
		currentLang,
	]);

	const accentColor = '#7c3aed';
	const accentBg = alpha(accentColor, 0.06);

	// Auto-expand when entering AI mode, collapse when leaving
	const handleToggleExpanded = () => {
		if (!aiMode) setExpanded((v) => !v);
	};

	return (
		<Paper
			elevation={0}
			sx={{
				border: '1px solid',
				borderColor: displayTranslation ? alpha(accentColor, 0.2) : '#e5e7eb',
				borderRadius: radius.md,
				overflow: 'hidden',
				mb: 2,
			}}>
			{/* ── Header ── */}
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				onClick={handleToggleExpanded}
				sx={{
					px: 2.5,
					py: 2,
					bgcolor: accentBg,
					borderBottom: expanded || aiMode ? '1px solid' : 'none',
					borderColor: alpha(accentColor, 0.1),
					cursor: aiMode ? 'default' : 'pointer',
					transition: 'all 0.15s ease',
					'&:hover': aiMode ? {} : { bgcolor: alpha(accentColor, 0.1) },
				}}>
				<Stack direction="row" alignItems="center" spacing={1.5}>
					<Box
						sx={{
							width: 44,
							height: 44,
							borderRadius: '50%',
							bgcolor: alpha(accentColor, 0.12),
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}>
						<IconSparkle sx={{ fontSize: 24, color: accentColor }} />
					</Box>
					<Box>
						<Typography sx={{ fontWeight: 700, fontSize: TEXT_LG, color: '#111827' }}>
							AI Summary
						</Typography>
						{subtitle && (
							<Typography sx={{ fontSize: TEXT_BASE, color: '#6B7280' }}>{subtitle}</Typography>
						)}
					</Box>
				</Stack>

				<Stack direction="row" spacing={1} alignItems="center">
					{needsTranslation && !aiMode && (
						<Tooltip title={`Translate to ${getLangName(currentLang)}`} arrow>
							<Button
								startIcon={
									translating ? (
										<CircularProgress size={18} color="inherit" />
									) : (
										<IconTranslate sx={{ fontSize: 20 }} />
									)
								}
								onClick={handleTranslate}
								disabled={translating}
								sx={{
									textTransform: 'none',
									fontWeight: 600,
									fontSize: TEXT_BASE,
									color: '#2563eb',
									borderRadius: radius.sm,
									'&:hover': { bgcolor: alpha('#2563eb', 0.08) },
								}}>
								{translating ? 'Translating\u2026' : 'Translate'}
							</Button>
						</Tooltip>
					)}
					{displayTranslation && !aiMode && (
						<Tooltip title="Regenerate summary" arrow>
							<Button
								startIcon={<IconRefresh sx={{ fontSize: 20 }} />}
								onClick={handleOpenAiMode}
								sx={{
									textTransform: 'none',
									fontWeight: 600,
									fontSize: TEXT_BASE,
									color: accentColor,
									borderRadius: radius.sm,
									'&:hover': { bgcolor: alpha(accentColor, 0.08) },
								}}>
								Regenerate
							</Button>
						</Tooltip>
					)}
					<IconExpand
						sx={{
							fontSize: 24,
							color: '#9CA3AF',
							transition: 'transform 0.2s',
							transform: expanded || aiMode ? 'rotate(180deg)' : 'rotate(0deg)',
						}}
					/>
				</Stack>
			</Stack>

			{/* ── Content (collapsible) ── */}
			<Collapse in={expanded || aiMode}>
				<Box sx={{ px: 2.5, py: 2.5 }}>
					{/* AI-Assisted Information disclaimer — surfaces the shared
					    disclaimer at the top of the AI output region. */}
					<AiDisclaimerBanner sx={{ mb: 2 }} />

					{/* Loading docs */}
					{docsLoading && (
						<Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
							<CircularProgress size={28} sx={{ color: accentColor }} />
							<Typography sx={{ fontSize: TEXT_BASE, color: 'text.secondary' }}>
								Loading documents…
							</Typography>
						</Stack>
					)}

					{/* No documents */}
					{!docsLoading && !hasDocs && !displayTranslation && (
						<Typography
							sx={{
								color: '#9CA3AF',
								fontSize: TEXT_BASE,
								textAlign: 'center',
								py: 4,
							}}>
							Upload documents to generate an AI summary.
						</Typography>
					)}

					{/* Has docs, no cached summary, not in AI mode → CTA */}
					{!docsLoading && hasDocs && !displayTranslation && !aiMode && (
						<Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
							<Typography sx={{ color: '#6B7280', fontSize: TEXT_BASE, textAlign: 'center' }}>
								{docsWithJobs.length} document{docsWithJobs.length !== 1 ? 's' : ''} available for
								AI analysis
							</Typography>
							<Button
								variant="contained"
								startIcon={<IconSparkle />}
								onClick={handleOpenAiMode}
								sx={{
									textTransform: 'none',
									fontWeight: 700,
									fontSize: TEXT_BASE,
									borderRadius: radius.sm,
									bgcolor: accentColor,
									'&:hover': { bgcolor: alpha(accentColor, 0.85) },
									px: 3,
									py: 1,
								}}>
								Run AI Summary
							</Button>
						</Stack>
					)}

					{/* Cached summary — render markdown */}
					{displayTranslation && !aiMode && (
						<>
							{/* Translation banner — current UI lang not yet cached */}
							{needsTranslation && (
								<Stack
									direction="row"
									alignItems="center"
									spacing={1.5}
									sx={{
										mb: 1.5,
										p: 1.5,
										bgcolor: alpha('#2563eb', 0.06),
										borderRadius: 1,
									}}>
									<IconTranslate sx={{ fontSize: 20, color: '#2563eb' }} />
									<Typography sx={{ flex: 1, fontSize: TEXT_BASE, color: '#374151' }}>
										Showing {getLangName(displayLang)} &mdash; no {getLangName(currentLang)}{' '}
										translation yet
									</Typography>
									<Button
										startIcon={
											translating ? (
												<CircularProgress size={16} color="inherit" />
											) : (
												<IconTranslate sx={{ fontSize: 18 }} />
											)
										}
										disabled={translating}
										onClick={handleTranslate}
										variant="contained"
										size="small"
										sx={{
											textTransform: 'none',
											fontWeight: 600,
											fontSize: TEXT_BASE,
											borderRadius: radius.sm,
											bgcolor: '#2563eb',
											'&:hover': { bgcolor: alpha('#2563eb', 0.85) },
										}}>
										{translating ? 'Translating\u2026' : `Translate to ${getLangName(currentLang)}`}
									</Button>
								</Stack>
							)}

							{/* Translate error (shown outside AI mode) */}
							{error && (
								<Stack
									direction="row"
									alignItems="center"
									spacing={1}
									sx={{
										mb: 1.5,
										bgcolor: alpha(theme.palette.error.main, 0.06),
										borderRadius: radius.sm,
										px: 2,
										py: 1.5,
									}}>
									<IconError sx={{ fontSize: 22, color: theme.palette.error.main }} />
									<Typography sx={{ fontSize: TEXT_BASE, color: theme.palette.error.main }}>
										{error}
									</Typography>
								</Stack>
							)}

							<Box sx={{ maxHeight: 500, overflow: 'auto' }}>
								<MarkdownRenderer>{displayTranslation.results}</MarkdownRenderer>
							</Box>
						</>
					)}

					{/* ── AI Mode Panel ── */}
					<Collapse in={aiMode}>
						<Stack spacing={2.5}>
							{/* Close button */}
							<Stack direction="row" justifyContent="space-between" alignItems="center">
								<Typography sx={{ fontWeight: 700, fontSize: TEXT_BASE, color: '#374151' }}>
									Configure AI Summary
								</Typography>
								<IconButton onClick={() => setAiMode(false)}>
									<IconClose sx={{ fontSize: 22 }} />
								</IconButton>
							</Stack>

							{/* Document selector */}
							<Box>
								<Stack
									direction="row"
									justifyContent="space-between"
									alignItems="center"
									sx={{ mb: 1.5 }}>
									<Typography
										sx={{
											fontWeight: 600,
											fontSize: TEXT_SECTION,
											color: '#374151',
											textTransform: 'uppercase',
											letterSpacing: '0.05em',
										}}>
										Documents & Datasets
									</Typography>
									{docInsights.length > 0 && (
										<Button
											startIcon={<IconSelectAll sx={{ fontSize: 18 }} />}
											onClick={toggleAll}
											sx={{
												textTransform: 'none',
												fontSize: TEXT_BASE,
												color: '#6B7280',
											}}>
											{(() => {
												const available = docInsights.filter((d) => d.hasInsights);
												const allDocsSelected =
													selectedDocs.size === available.length && available.length > 0;
												const allOn = allDocsSelected && includeContractData && includePrompt;
												return allOn ? 'Deselect All' : 'Select All';
											})()}
										</Button>
									)}
								</Stack>

								{insightsLoading ? (
									<Stack spacing={1.5}>
										<LinearProgress
											sx={{
												borderRadius: 1,
												'& .MuiLinearProgress-bar': { bgcolor: accentColor },
											}}
										/>
										<Typography
											sx={{
												fontSize: TEXT_BASE,
												color: 'text.secondary',
												textAlign: 'center',
											}}>
											Fetching document insights…
										</Typography>
									</Stack>
								) : (
									<Stack spacing={0.5}>
										{docInsights.map((doc) => (
											<Stack
												key={doc.docId}
												direction="row"
												alignItems="center"
												spacing={1.5}
												sx={{
													py: 0.75,
													px: 1.5,
													borderRadius: 1,
													'&:hover': {
														bgcolor: doc.hasInsights ? alpha(accentColor, 0.04) : undefined,
													},
												}}>
												<Checkbox
													checked={selectedDocs.has(doc.docId)}
													disabled={!doc.hasInsights}
													onChange={() => toggleDoc(doc.docId)}
													sx={{
														p: 0.5,
														color: alpha(accentColor, 0.4),
														'&.Mui-checked': { color: accentColor },
													}}
												/>
												<IconDoc
													sx={{
														fontSize: 22,
														color: doc.hasInsights ? '#6B7280' : '#D1D5DB',
													}}
												/>
												<Typography
													sx={{
														flex: 1,
														fontSize: TEXT_BASE,
														fontWeight: 500,
														color: doc.hasInsights ? '#374151' : '#9CA3AF',
													}}
													noWrap>
													{doc.filename}
												</Typography>
												{doc.hasInsights ? (
													<Chip
														label={`${(Math.min(doc.textLength, MAX_CHARS_PER_DOC) / 1000).toFixed(
															1
														)}k chars`}
														sx={{
															height: 28,
															fontSize: TEXT_BASE,
															bgcolor: alpha(accentColor, 0.08),
															color: accentColor,
															fontWeight: 600,
														}}
													/>
												) : (
													<Tooltip title="No AI insights available — process this document first">
														<Typography
															sx={{
																fontSize: TEXT_BASE,
																color: '#D1D5DB',
																fontStyle: 'italic',
															}}>
															No insights
														</Typography>
													</Tooltip>
												)}
											</Stack>
										))}

										{/* Docs without job_ids */}
										{contractDocs
											.filter((d) => !d.job_id)
											.map((doc) => (
												<Stack
													key={doc.id}
													direction="row"
													alignItems="center"
													spacing={1.5}
													sx={{ py: 0.75, px: 1.5 }}>
													<Checkbox disabled sx={{ p: 0.5 }} />
													<IconDoc sx={{ fontSize: 22, color: '#D1D5DB' }} />
													<Typography
														sx={{
															flex: 1,
															fontSize: TEXT_BASE,
															color: '#9CA3AF',
														}}
														noWrap>
														{doc.filename}
													</Typography>
													<Typography
														sx={{
															fontSize: TEXT_BASE,
															color: '#D1D5DB',
															fontStyle: 'italic',
														}}>
														Not processed
													</Typography>
												</Stack>
											))}
									</Stack>
								)}
							</Box>

							<Divider />
							{/* Include Contract Data toggle */}
							<Stack
								direction="row"
								alignItems="center"
								spacing={1.5}
								sx={{
									py: 1,
									px: 1.5,
									borderRadius: 1,
									cursor: 'pointer',
									'&:hover': { bgcolor: alpha(accentColor, 0.04) },
								}}
								onClick={() => setIncludeContractData((v) => !v)}>
								<Checkbox
									checked={includeContractData}
									sx={{
										p: 0.5,
										color: alpha(accentColor, 0.4),
										'&.Mui-checked': { color: accentColor },
									}}
								/>
								<IconJson
									sx={{ fontSize: 22, color: includeContractData ? accentColor : '#9CA3AF' }}
								/>
								<Box sx={{ flex: 1 }}>
									<Typography sx={{ fontWeight: 600, fontSize: TEXT_BASE, color: '#374151' }}>
										Include Contract Data
									</Typography>
									<Typography sx={{ fontSize: TEXT_BASE, color: '#9CA3AF' }}>
										Send amounts, entities, milestones &amp; payment progress as context
									</Typography>
								</Box>
								<Chip
									label={`~${(contractDataChars / 1000).toFixed(1)}k chars`}
									sx={{
										height: 28,
										fontSize: TEXT_BASE,
										bgcolor: alpha(accentColor, includeContractData ? 0.08 : 0.04),
										color: includeContractData ? accentColor : '#9CA3AF',
										fontWeight: 600,
									}}
								/>
							</Stack>

							<Divider />
							{/* Prompt toggle + editor */}
							<Stack
								direction="row"
								alignItems="center"
								spacing={1.5}
								sx={{
									py: 1,
									px: 1.5,
									borderRadius: 1,
									cursor: 'pointer',
									'&:hover': { bgcolor: alpha(accentColor, 0.04) },
								}}
								onClick={() => setIncludePrompt((v) => !v)}>
								<Checkbox
									checked={includePrompt}
									sx={{
										p: 0.5,
										color: alpha(accentColor, 0.4),
										'&.Mui-checked': { color: accentColor },
									}}
								/>
								<IconEdit sx={{ fontSize: 22, color: includePrompt ? accentColor : '#9CA3AF' }} />
								<Box sx={{ flex: 1 }}>
									<Typography sx={{ fontWeight: 600, fontSize: TEXT_BASE, color: '#374151' }}>
										Include Prompt
									</Typography>
									<Typography sx={{ fontSize: TEXT_BASE, color: '#9CA3AF' }}>
										Instructions for the AI on how to analyze and structure the summary
									</Typography>
								</Box>
								<Stack direction="row" alignItems="center" spacing={1}>
									<Chip
										label={`~${(prompt.length / 1000).toFixed(1)}k chars`}
										sx={{
											height: 28,
											fontSize: TEXT_BASE,
											bgcolor: alpha(accentColor, includePrompt ? 0.08 : 0.04),
											color: includePrompt ? accentColor : '#9CA3AF',
											fontWeight: 600,
										}}
									/>
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											setShowPromptEditor((v) => !v);
										}}
										sx={{ color: '#9CA3AF' }}>
										<IconExpand
											sx={{
												fontSize: 20,
												transition: 'transform 0.2s',
												transform: showPromptEditor ? 'rotate(180deg)' : 'rotate(0deg)',
											}}
										/>
									</IconButton>
								</Stack>
							</Stack>

							<Collapse in={showPromptEditor}>
								<Box sx={{ mt: 1, ml: 6 }}>
									<TextField
										multiline
										minRows={4}
										maxRows={12}
										fullWidth
										value={prompt}
										onChange={(e) => setPrompt(e.target.value)}
										variant="outlined"
										sx={{
											'& .MuiOutlinedInput-root': {
												fontSize: TEXT_BASE,
												fontFamily: 'monospace',
												borderRadius: radius.sm,
											},
										}}
									/>
									<Button
										onClick={() => setPrompt(DEFAULT_PROMPT)}
										sx={{
											mt: 0.5,
											textTransform: 'none',
											fontSize: TEXT_BASE,
											color: '#9CA3AF',
										}}>
										Reset to default
									</Button>
								</Box>
							</Collapse>

							{/* Error */}
							{error && (
								<Stack
									direction="row"
									alignItems="center"
									spacing={1}
									sx={{
										bgcolor: alpha(theme.palette.error.main, 0.06),
										borderRadius: radius.sm,
										px: 2,
										py: 1.5,
									}}>
									<IconError sx={{ fontSize: 22, color: theme.palette.error.main }} />
									<Typography sx={{ fontSize: TEXT_BASE, color: theme.palette.error.main }}>
										{error}
									</Typography>
								</Stack>
							)}

							{/* Generate button */}
							<Stack direction="row" justifyContent="flex-end" spacing={1.5}>
								<Button
									onClick={() => setAiMode(false)}
									sx={{
										textTransform: 'none',
										fontWeight: 600,
										fontSize: TEXT_BASE,
										color: '#6B7280',
										borderRadius: radius.sm,
									}}>
									Cancel
								</Button>
								<Button
									variant="contained"
									startIcon={
										generating ? <CircularProgress size={20} color="inherit" /> : <IconSparkle />
									}
									disabled={generating}
									onClick={handleGenerate}
									sx={{
										textTransform: 'none',
										fontWeight: 700,
										fontSize: TEXT_BASE,
										borderRadius: radius.sm,
										bgcolor: accentColor,
										'&:hover': { bgcolor: alpha(accentColor, 0.85) },
										px: 3,
										py: 1,
									}}>
									{generating ? 'Generating…' : 'Generate Summary'}
								</Button>
							</Stack>
						</Stack>
					</Collapse>
				</Box>
			</Collapse>
		</Paper>
	);
};
